import { useState, useEffect } from "react";
import { useApi } from '../../../api/ApiProvider';
import Api from '../../../axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { formatDate } from "common/utils";
import intl from 'components/i18n/ReactIntlWrapper';
import { Box, Typography, Checkbox, Grid } from '@mui/material';
import Spinner from '../../Spinner';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import { handleAPIError } from "common/errorHandler";
import { DataGrid, GridColDef, GridInitialState, GridSortModel } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION } from 'hooks/useSearch';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

const CurrentAssignations = ({ idEmployee }) => {

    const [state, setState] = useState({
        idEmployee: idEmployee,
        currentPage: 1
    });

    const api: Api = useApi();

    const toast = useToastMessageQueue();

    const [assignationsByEmployee, setAssignationsByEmployee] = useState({
        totalRecords: null,
		    queryResult: []
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({});
    const [inactiveAssignations, setInactiveAssignations] = useState(false);

    const [pageCount, setpageCount] = useState<number>(0);


    const [limit, setLimit] = useState<number>(() => {
		return parseInt(localStorage.getItem('itemsPerPageCurrentAssignations'), 10) || 10;
	  });

    const [pagination, setPagination] = usePaginationPreference('currentAssignations', 10);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'Employee', sort: 'asc' }]);
    
    
    const handleSortModelChange = (model: GridSortModel) => {
      setSortModel(model);
    };

    useEffect(() => {
		setLimit(parseInt(localStorage.getItem('itemsPerPageCurrentAssignations'), 10) || 10);
	  }, []);

    const fetchCurrentAssignationsByEmployee = async (idEmployee: number) => {
        setLoading(true);
        const response = await api.projectApi.apiVversionEmployeeProjectGetByEmployeeIdIdGet(idEmployee, "1", {}).then((response)=>{
                    setAssignationsByEmployee({
                        totalRecords: response.data.data.totalRecords,
                        queryResult: response.data.data.employeeProjects
                    });
                }).catch((error) => {
                    handleAPIError(error, toast, errors);    
                    setErrors({ ...errors });            
                });
        setLoading(false);
    };

    useEffect(() => {
        if (idEmployee) {
            fetchCurrentAssignationsByEmployee(idEmployee);
        };
    }, [idEmployee]);

    const handleInactiveAssignations = () => {
        setInactiveAssignations(!inactiveAssignations);
        setState({ ...state, currentPage: 1 });
    };

    const currentDate = new Date();
    const currentDateFormatted = currentDate.toISOString();

    const INITIAL_STATE: GridInitialState = {
	    pagination: {
	      paginationModel: {
	    	page: DEFAULT_PAGINATION.pageNum,
	    	pageSize: DEFAULT_PAGINATION.pageSize,
	      },
	    },
	    sorting: {
	      sortModel: [
		    {
		      field: 'Project',
		      sort: 'asc',
		    },
	      ],
	    },
	  };

    const columns: GridColDef[] = [
        {
          field: 'projectName',
          headerName: intl.get('currentAssignations.table.thead.project'),
          width: 250,
          flex: 1,
        },
        {
          field: 'hourlyRate',
          headerName: intl.get('currentAssignations.table.thead.hourlyRate'),
          width: 250,
          flex: 1,
          
        },
        {
          field: 'monthlyRate',
          headerName: intl.get('currentAssignations.table.thead.monthlyRate'),
          width: 250,
          flex: 1,
        },
        {
          field: 'allocation',
          headerName: intl.get('currentAssignations.table.thead.allocation'),
          width: 250,
          flex: 1,
          renderCell: (params) => <>{params.row.allocation} {params.row.allocationType === 1 ? ' %' : ' hs.'}</>,
        },
        {
          field: 'role',
          headerName: intl.get('currentAssignations.table.thead.role'),
          width: 250,
          flex: 1,
          renderCell: (params) => <>{params.row.role?.name || ''}</>,
        },
        {
          field: 'startEndDate',
          headerName: `${intl.get('currentAssignations.table.thead.startDate')} ${intl.get('currentAssignations.table.thead.dueDate')}`,
          width: 250,
          flex: 1,
          renderCell: (params) => {
            const startDate = params.row.startDate ? formatDate(new Date(params.row.startDate)) : '';
            const dueDate = params.row.employee?.dueDate ? formatDate(new Date(params.row.employee.dueDate)) : '';
            return <>{startDate}-{dueDate}</>;
          },
        },
      ];

    return (
      <>
        <Box mb={2}>
          <Typography variant="h6" color="primary" display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <ArticleOutlinedIcon />
            {intl.get('currentAssignations.header')}
          </Typography>
        </Box>
        {loading ? (
          <Box style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner small={true} />
          </Box>
        ) : (
          <Box>
            <Box sx={{ overflowX: 'auto', minHeight: 400 }}>
              <Box style={{ width: '100%', display: 'flex' }}>
                <DataGrid
                  rows={inactiveAssignations ? assignationsByEmployee.queryResult : assignationsByEmployee.queryResult?.filter((assignation) => assignation.dueDate >= currentDateFormatted)}
                  columns={columns}
                  getRowId={(row) => row.idProject}
                  rowCount={assignationsByEmployee?.totalRecords || 0}
                  loading={loading}
                  initialState={INITIAL_STATE}
                  paginationModel={pagination}
                  onPaginationModelChange={setPagination}
                  sortModel={sortModel}
                  onSortModelChange={handleSortModelChange}
                  pageSizeOptions={[5, 10, 20]}
                />
            </Box>
            </Box>
              
            <Box display="flex" alignItems="center" mt={2}>
              <Checkbox onClick={handleInactiveAssignations} />
              <Typography variant="body2">{intl.get('currentAssignations.checkbox.inactive')}</Typography>
            </Box>
          </Box>
        )}
      </>
    );
}       

export default CurrentAssignations;